/**
 * @documental
 * @export Field
 * @name
 * Passwordbox.Field
 */
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { checkPasswordStrength } from "capitalrx-utilities";

import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { SubmitGroup } from "../SubmitGroup";
import { FieldProps as BaseFieldProps } from "../Textbox/Field";
import { Input } from "../Textbox/Input";
import { ValidityGroup } from "../ValidityGroup";
import { DisabledLengthRule, DisabledRequiredRule, length as lengthRule, required as requiredRule } from "../validation/rules";
import { PasswordboxProps } from "./Passwordbox";

/**
 * The `Field` component properties.
 */

export interface FieldProps extends BaseFieldProps, Pick<PasswordboxProps, "showToggle"> {
  /**
   * The minimum number of characters allowed.
   */
  readonly minLength?: number;

  /**
   * Whether or not the field should enforce validation
   */
  readonly enforceStrength?: boolean;
}

/**
 * A React Hook Form compatible Passwordbox Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, required = false, minLength, maxLength, defaultValue = null, validator, enforceStrength = true, ...restProps } = props;

  const elementRef = useRef<HTMLInputElement | null>(null);
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const validateHandler = useCallback(
    (value: string | null, formValues: FieldValues) => {
      if (value === null || value === "") return true;

      const { length, lowercase, number, specialChar, uppercase } = checkPasswordStrength(value, minLength || 15);

      const errors: Array<string> = [];

      if (!length) {
        errors.push(`at least ${minLength || 15} characters`);
      }

      if (!lowercase) {
        errors.push("at least 1 lowercase letter");
      }

      if (!number) {
        errors.push("at least 1 number");
      }

      if (!specialChar) {
        errors.push("at least 1 special character");
      }

      if (!uppercase) {
        errors.push("at least 1 uppercase letter");
      }

      if (errors.length > 1) {
        errors[errors.length - 1] = "and " + errors[errors.length - 1];
      }

      if (errors.length > 0) {
        const separator = errors.length === 2 ? " " : ", ";
        return `Password must contain ${errors.join(separator)}.`;
      }

      return validator ? validator(value, formValues) : true;
    },
    [validator, minLength],
  );

  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
      ...(disabled || readOnly ? DisabledLengthRule : lengthRule(minLength, maxLength)),
      validate: disabled || readOnly || enforceStrength === false ? undefined : validateHandler,
    }),
    [disabled, readOnly, required, minLength, maxLength, validateHandler, enforceStrength],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  const refHandler = useCallback(
    (element: HTMLInputElement | null) => {
      elementRef.current = element;
      element?.removeAttribute("value");

      ref?.(element);
    },
    [ref],
  );

  useLayoutEffect(() => {
    setTimeout(() => {
      elementRef.current?.removeAttribute("value");
    });
  }, [value]);

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={refHandler}
          value={value}
          type="password"
          required={required}
          maxLength={maxLength}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocusExit={onBlur}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
